import { useEffect, useRef, useState } from 'react';
import { ConfigProvider, Drawer } from 'antd';
import heIL from 'antd/locale/he_IL';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faPrint, faXmark } from '@fortawesome/free-solid-svg-icons';
import { Loader } from '../../components/Loader';
import { getToken } from '../../api/apiFetch';

interface Props {
  open: boolean;
  title: string;
  /** API path that streams the PDF blob, e.g. `/register-closings/12/pdf-blob`. */
  blobPath: string | null;
  downloadName: string;
  onClose: () => void;
}

/**
 * Lightweight single-pane PDF viewer drawer, reused by the closing report and
 * the bank-deposit report. Fetches the PDF as a same-origin blob (so
 * iframe.print() works) and offers download + print. The receipt-based
 * deposit slip keeps its own two-pane DepositPdfDrawer.
 */
export function PdfDrawer({ open, title, blobPath, downloadName, onClose }: Props) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!open || !blobPath) {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    let createdUrl: string | null = null;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const token = getToken();
        const res = await fetch(`/api${blobPath}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      } catch (err) {
        if (!cancelled) setError((err as Error).message || 'שגיאה');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, blobPath]);

  const onDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = downloadName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const onPrint = () => {
    const w = iframeRef.current?.contentWindow;
    if (!w) return;
    try {
      w.focus();
      w.print();
    } catch (err) {
      window.alert('שגיאה בפתיחת הדפסה: ' + (err as Error).message);
    }
  };

  return (
    <Drawer
      title={null}
      placement="left"
      open={open}
      onClose={onClose}
      width="min(95vw, 900px)"
      closable={false}
      styles={{ body: { padding: 0 } }}
    >
      <ConfigProvider locale={heIL} getPopupContainer={() => document.body}>
        <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-white">
          <div className="text-heading-3-bold text-main-text">{title}</div>
          <div className="flex items-center gap-2">
            <HeaderButton icon={faDownload} label="הורדה" onClick={onDownload} disabled={!blobUrl} />
            <HeaderButton icon={faPrint} label="הדפסה" onClick={onPrint} disabled={!blobUrl} primary />
            <button
              onClick={onClose}
              aria-label="סגור"
              className="p-2 rounded hover:bg-grey-bg text-secondary-text hover:text-main-text transition-colors"
            >
              <FontAwesomeIcon icon={faXmark} style={{ fontSize: 16 }} />
            </button>
          </div>
        </div>
        <div className="bg-grey-bg relative" style={{ height: 'calc(100vh - 56px)' }}>
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader />
            </div>
          )}
          {error && !loading && (
            <div className="absolute inset-0 flex items-center justify-center text-error-red text-small px-6 text-center">
              שגיאה בטעינת ה-PDF: {error}
            </div>
          )}
          {blobUrl && (
            <iframe
              ref={iframeRef}
              src={`${blobUrl}#toolbar=0&navpanes=0&view=FitH`}
              title="PDF preview"
              className="w-full h-full border-0 block"
            />
          )}
        </div>
      </ConfigProvider>
    </Drawer>
  );
}

function HeaderButton({
  icon,
  label,
  onClick,
  disabled,
  primary,
}: {
  icon: any;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-2 px-3 py-1.5 rounded text-small font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
        primary
          ? 'bg-primary text-white hover:bg-primary-hover'
          : 'bg-white border border-border text-main-text hover:border-primary'
      }`}
    >
      <FontAwesomeIcon icon={icon} style={{ fontSize: 13 }} />
      <span>{label}</span>
    </button>
  );
}
